/* eslint-disable no-useless-constructor*/

/**
 * Copyright (C) 2025 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

export type BeamCoordinates = {
  lat: number;
  lng: number;
};

export default class Beam {
  id: string;
  displayName: string;
  // Each array will contain a contour as the first array of coordinates and any subsequent arrays
  // will hold the holes for that contour
  /**
   * See the following as an example:
   * [
        [
            [[longitude,latitude],...],  <-- polygon 1
            [[longitude,latitude],...],  <-- hole
            [[longitude,latitude],...]   <-- hole
        ],
        [
            [[longitude,latitude],...], <-- polygon 2
            [[longitude,latitude],...], <-- hole
            [[longitude,latitude],...], <-- hole
        ],
    ]
   */
  geoJsonCoordinates: BeamCoordinates[][][]; 
  priority: number;
  sedVersion: string;
  sscfVersion: string;

  constructor(
    id: string,
    displayName: string,
    geoJsonCoordinates: BeamCoordinates[][][],
    priority: number,
    sedVersion: string,
    sscfVersion: string,
  ) {
    this.id = id;
    this.displayName = displayName;
    this.geoJsonCoordinates = geoJsonCoordinates;
    this.priority = priority;
    this.sedVersion = sedVersion;
    this.sscfVersion = sscfVersion;
  }
}

export class AtiBeam extends Beam {}

export class HnaBeam extends Beam {
  // Add here additional HnaBeam properties if necessary
  hnaAltitude: number;

  constructor(
    id: string,
    displayName: string,
    geoJsonCoordinates: BeamCoordinates[][][],
    priority: number,
    hnaAltitude: number,
    sedVersion: string,
    sscfVersion: string,
  ) {
    super(id, displayName, geoJsonCoordinates, priority, sedVersion, sscfVersion);

    this.hnaAltitude = hnaAltitude;
  }
}

export type AnyBeam = Beam | AtiBeam | HnaBeam;

export const isAtiBeam = (beam: AnyBeam): boolean => {
  return beam instanceof AtiBeam;
};

export const isHnaBeam = (beam: AnyBeam): boolean => {
  return beam instanceof HnaBeam;
};

//will be used to pull latest VI HNA zones when in fleet view
export const LATEST = 'latest';
export const AL_MAP_VERSION_PREFIX = 'al-';
export const VIHNA_MAP_VERSION_PREFIX = 'vihna-';
export const VIHNA_LATEST = VIHNA_MAP_VERSION_PREFIX + LATEST;
export const GX_LATEST_VERSION = `gx`;

export enum ContourType {
  VGNET = 'VGNET',
  HCX = 'I5_HCX',
  VIASAT = 'VIASAT',
  HNA = 'HNA',
  KA_HNA = 'KA_HNA',
  HCX_HNA = 'HCX_HNA',
  VIASAT_3 = 'VIASAT_3',
  GX = 'GX',
  GX_HNA = 'GX_HNA',
  RTE = 'RTE',
  OTHER = 'OTHER',
}

export enum Network {
  VGNET = 'VGNET',
  I5_HCX = 'I5_HCX',
  VIASAT = 'VIASAT',
  VIASAT_3 = 'VIASAT_3',
  GX = 'GX',
  RTE = 'RTE',
  OTHER = 'OTHER',
}

export enum MapType {
  COVERAGE = 'COVERAGE',
  FL_HEAT_MAP = 'FL_HEAT_MAP',
  RL_HEAT_MAP = 'RL_HEAT_MAP',
  HNA = 'HNA'
}

/**
 * Dark Theme (This is the only one in use, in insights)
 *
 * First 3 values are RGB, 4th value is an optional value which is divided by 255.
 * Defaults for opacity are passed to getDefaultColorFromFeature, and is 97/255 for dark and light theme which is 0.38
 *
 * Optional 4th value opacity examples
 *  255/255=1 which is 100% opacity,
 *  128/255=0.5 is 50% opacity
 */
export const darkThemeBeamRGBValues = {
  VIASAT_3: [0, 0, 255], // VS3 Blue, defaults to 97 / 255 = .38 opactiy
  ka: [231, 86, 244], //#E756F4
  ku: [114, 185, 255], //#72B9FF
  kuHeatmap: [114, 185, 255, 97], //#72B9FFcc
  hcx: [97, 222, 216], //#61DED8
  hnaAtAltitude: [255, 109, 0], //#FF6D00
  hnaAllAirspaceAltitude: [191, 13, 13], //#BF0D0D
  hnaWeightOnWheels: [195, 187, 38], // #C3BB26
  gx: [253, 253, 150], //TODO: update after keith's design
  rte: [159, 175, 188], //#9FAFBC
};

/**
 * Light Theme (Not used anymore, was for MPT, but keep)
 */
export const lightThemeBeamRGBValues = {
  VIASAT_3: [0, 0, 255], // VS3 Blue, defaults to 97 / 255 = .38 opactiy
  ka: [234, 123, 206], //#EA7BCE
  ku: [80, 147, 192], //#5093c0
  kuHeatmap: [80, 147, 192, 191], //#5093c0cc
  hcx: [70, 89, 103], //#465967
  hnaAtAltitude: [255, 109, 0], //#FF6D00
  hnaAllAirspaceAltitude: [191, 13, 13], //#BF0D0D
  hnaWeightOnWheels: [195, 187, 38], // #C3BB26
  gx: [255, 255, 153], //TODO: update after keith's design
  rte: [159, 175, 188], //#9FAFBC
};